import axios from "axios";
import BaseApi from "../BaseApi";
import CertificateAPI from "../Certificate";
import { TransitionCompleteResponse } from "src/Types/Task";

export type CreateAndSendResponseType = TransitionCompleteResponse | RosReestrSignDataType;

type RosReestrStartSignDataType = {
	fileId: string; 
	componentId: string; 
	useDefaultEpSp: boolean;
};

type RosReestrSignWithCertDataType = RosReestrStartSignDataType & {
	thumbprint: string;
	certificate: string;
};

type RosReestrDigestType = {
	code: string;
	value: string;
};

type RosReestrSignDataType = RosReestrSignWithCertDataType & {
	digests: Array<RosReestrDigestType>;
	hashAlgId: number;
	oldDigests?: Array<RosReestrDigestType>;
};

export default class RosreestrZipAPI extends BaseApi {

	static sign = async ( fileId: string, componentId: string, useDefaultEpSp: boolean, thumbprint: string ): Promise<TransitionCompleteResponse> => {
		const data = { fileId, componentId, useDefaultEpSp };
		if ( useDefaultEpSp && !thumbprint ) {
			return await this._getDefaultSign( data, thumbprint );
		} 
		else {
			return await this._sign( data, thumbprint )
		}
	};

	static _getDefaultSign = async ( data: RosReestrStartSignDataType, thumbprint: string ): Promise<TransitionCompleteResponse> => {
		const url = window.host + "/RosreestrZip/GetDefaultEpSpSign";
		try {
			const request = await axios.post( url, data, { withCredentials: true });
			if ( request.data.hasError ) {
				//При сообщении от сервера, что компонент в работе, выходим
				if ( request.data.errorMessage && request.data.errorMessage.includes('Данная операция уже выполняется. Пожалуйста, ожидайте.') ) {
					this.showToast( false, request.data.errorMessage );
					return {
						recid: data.fileId,
						refreshtask: false,
						hasError: false,
						refreshattach: false,
						kickuser: false,
						errorMessage: "Данная операция уже выполняется. Пожалуйста, ожидайте."
					};
				}
				if (!thumbprint) {
					throw new Error("NEED_CERTIFICATE");
				}
				throw new Error(request.data.errorMessage || "Неизвестная ошибка");
			}
			else {
				return request.data;
			}
		}
		catch (error: any) {
			throw error;
		}
	};

	static _sign = async ( data: RosReestrStartSignDataType, thumbprint: string ): Promise<TransitionCompleteResponse> => {
		const certBase64 = (await CertificateAPI.getCertInfoByThumbprint( thumbprint )).base64;
		const newData: RosReestrSignWithCertDataType = { ...data, thumbprint, certificate: certBase64 };
		const hash = await this._getHashFromServer( newData );
		return await this._signWithHash( hash );
	};

	static _getHashFromServer = async ( data: RosReestrSignWithCertDataType ): Promise<RosReestrSignDataType> => {
		const url = window.host + "/RosreestrZip/GetHashes";
		try {
			const request = await axios.post( url, data, { withCredentials: true } );
			return {
				...data,
				digests: request.data.digests,
				hashAlgId: request.data.hashAlgId
			};
		}
		catch (error: any) {
			throw error;
		}
	};

	static _signHashes = async ( data: RosReestrSignDataType ): Promise<RosReestrSignDataType> => {
		const newData: RosReestrSignDataType = { ...data, digests: [] };
		try {
			for (let index = 0; index < data.digests.length; index++) {
				const digest = data.digests[index];
				const signResult = await CertificateAPI.signHash( digest.value, data.hashAlgId, data.thumbprint );
				if (!signResult.success) {
					throw new Error( "Ошибка подписи: " + signResult.message );
				}
				newData.digests.push({ code: digest.code, value: signResult.data });
			}
			return newData;
		}
		catch (error: any) {
			throw new Error( "Ошибка подписи. " + error.message );
		}
	};

	static _signWithHash = async ( data: RosReestrSignDataType, previousData?: RosReestrSignDataType ): Promise<TransitionCompleteResponse> => {
		const signHashData = await this._signHashes( data );
		return await this._completeSign( signHashData, previousData );
	};

	static _completeSign = async ( data: RosReestrSignDataType, previousData?: RosReestrSignDataType ): Promise<TransitionCompleteResponse> => {
		const url = window.host + "/RosreestrZip/ReturnSigns";
		if ( !!previousData ) {
			data.oldDigests = previousData.digests;
		}
		const result = await axios.post( url, data, { withCredentials: true } );
		const responseData = result.data;
		if ( responseData.hasError ) {
			throw new Error( responseData.errorMessage );
		}
		else if ( !!responseData.recid ) {
			return responseData;
		}
		else {
			const newData = {
				...responseData,
				certificate: data.certificate,
				fileId: data.fileId,
				componentId: data.componentId,
			};
			return await this._signWithHash( newData, data );
		}
	};

};
